import { create } from 'zustand';
import { LEGACY_ID_TOKEN_STORAGE_KEY, LOCAL_STORAGE_KEYS } from '@/constants';
import type { SessionTenantType, SignInResponse } from '@bliks-io/contracts';

export type AuthSessionData = SignInResponse;

type SessionTenant = SignInResponse['tenant'];

interface AuthState {
  token: string;
  // True from logout click until /login is reached (or the page unloads into
  // the IdP redirect). Drives the full-screen splash and mutes the global
  // 401/403 handlers. In-memory ONLY — persisting it would strand the next
  // page load behind a stuck splash if an SSO redirect is killed mid-flight.
  isLoggingOut: boolean;
  // True from workspace-switch click until the full page load into the new
  // workspace (or until the switch fails). Drives the switch splash;
  // in-memory only for the same reason as isLoggingOut.
  isSwitchingWorkspace: boolean;
  tenant: SessionTenant | null;
  tenantType: SessionTenantType | null;

  // Computed
  isSuperAdmin: () => boolean;
  isPartner: () => boolean;
  slug: () => string;

  // Actions
  setAuth: (data: AuthSessionData) => void;
  clearAuth: () => void;
  beginLogout: () => void;
  finishLogout: () => void;
  beginWorkspaceSwitch: () => void;
  endWorkspaceSwitch: () => void;
  initFromStorage: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: '',
  isLoggingOut: false,
  isSwitchingWorkspace: false,
  tenant: null,
  tenantType: null,

  isSuperAdmin: () => {
    return get().tenantType === 'super';
  },

  isPartner: () => {
    return get().tenantType === 'partner';
  },

  slug: () => {
    return get().tenant?.slug ?? '';
  },

  setAuth: (data) => {
    set({
      token: data.token,
      tenant: data.tenant,
      tenantType: data.tenantType
    });

    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, data.token);
    localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT, JSON.stringify(data.tenant));
    localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_TYPE, data.tenantType);
  },

  beginLogout: () => set({ isLoggingOut: true }),
  finishLogout: () => set({ isLoggingOut: false }),

  beginWorkspaceSwitch: () => set({ isSwitchingWorkspace: true }),
  endWorkspaceSwitch: () => set({ isSwitchingWorkspace: false }),

  clearAuth: () => {
    // Clear state — deliberately NOT isLoggingOut: teardown happens in the
    // middle of the logout flow and the splash must outlive it.
    set({
      token: '',
      tenant: null,
      tenantType: null
    });

    for (const key of Object.values(LOCAL_STORAGE_KEYS)) {
      localStorage.removeItem(key);
    }
  },

  initFromStorage: () => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN) || '';

    const tenantJson = localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT);
    let tenant: SessionTenant | null = null;
    if (tenantJson) {
      try {
        // A session persisted before `name` existed carries none; the next
        // token renewal writes it back, so an empty display name for one
        // session beats stranding a returning user.
        const stored = JSON.parse(tenantJson) as Partial<SessionTenant>;
        tenant = {
          _id: stored._id ?? '',
          slug: stored.slug ?? '',
          name: stored.name ?? '',
          expiresAt: stored.expiresAt ?? null
        };
      } catch (e) {
        // Invalid JSON, keep tenant as null
        console.error('Failed to parse tenant from storage:', e);
      }
    }

    const tenantType = localStorage.getItem(
      LOCAL_STORAGE_KEYS.TENANT_TYPE
    ) as SessionTenantType | null;

    // One-time cleanup: the id_token used to persist here as the logout hint.
    // It now lives server-side, so drop any copy left by a pre-migration login.
    localStorage.removeItem(LEGACY_ID_TOKEN_STORAGE_KEY);

    set({
      token,
      tenant,
      tenantType
    });
  }
}));

/**
 * Covers a workspace switch with the splash from the request until the page
 * unloads; a failed switch drops the splash so the current workspace stays
 * usable, and the caller still sees the error.
 */
export async function withWorkspaceSwitchSplash<T>(run: () => Promise<T>): Promise<T> {
  useAuthStore.getState().beginWorkspaceSwitch();
  try {
    return await run();
  } catch (error) {
    useAuthStore.getState().endWorkspaceSwitch();
    throw error;
  }
}
